import { useState, useEffect, useRef } from 'react';
import { Save, Loader2, Upload, Trash2 } from 'lucide-react';
import api from '../../../services/api';

interface Empresa {
  id: number;
  nome: string;
  cnpj: string;
  email?: string;
  telefone?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  logo?: string;
}

export function EmpresaConfig() {
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEmpresa();
  }, []);

  const loadEmpresa = async () => {
    try {
      const response = await api.get('/admin/empresa');
      setEmpresa(response.data);
    } catch (error) {
      console.error('Erro ao carregar:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresa) return;

    setIsSaving(true);
    setMessage(null);
    try {
      await api.put('/admin/empresa', empresa);
      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !empresa) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Imagem deve ter no máximo 2MB' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setEmpresa({ ...empresa, logo: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    if (empresa) {
      setEmpresa({ ...empresa, logo: undefined });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
      </div>
    );
  }

  if (!empresa) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Logo */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 border-b pb-2 mb-4">Logotipo</h3>
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
            {empresa.logo ? (
              <img src={empresa.logo} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-gray-400 text-sm text-center px-2">Sem logo</span>
            )}
          </div>
          <div className="space-y-2">
            <input type="file" ref={fileInputRef} onChange={handleLogoUpload} accept="image/*" className="hidden" />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
              <Upload className="w-4 h-4" /> Enviar Logo
            </button>
            {empresa.logo && (
              <button type="button" onClick={removeLogo}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                <Trash2 className="w-4 h-4" /> Remover
              </button>
            )}
            <p className="text-xs text-gray-500">PNG, JPG ou SVG. Máximo 2MB.</p>
          </div>
        </div>
      </div>


      {/* Dados da Empresa */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 border-b pb-2 mb-4">Dados da Empresa</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa *</label>
            <input type="text" required value={empresa.nome} onChange={e => setEmpresa({ ...empresa, nome: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
            <input type="text" required value={empresa.cnpj} onChange={e => setEmpresa({ ...empresa, cnpj: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" placeholder="00.000.000/0001-00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={empresa.email || ''} onChange={e => setEmpresa({ ...empresa, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <input type="text" value={empresa.telefone || ''} onChange={e => setEmpresa({ ...empresa, telefone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" placeholder="(00) 0000-0000" />
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 border-b pb-2 mb-4">Endereço</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
            <input type="text" value={empresa.cep || ''} onChange={e => setEmpresa({ ...empresa, cep: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
            <input type="text" value={empresa.logradouro || ''} onChange={e => setEmpresa({ ...empresa, logradouro: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
            <input type="text" value={empresa.numero || ''} onChange={e => setEmpresa({ ...empresa, numero: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
            <input type="text" value={empresa.complemento || ''} onChange={e => setEmpresa({ ...empresa, complemento: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
            <input type="text" value={empresa.bairro || ''} onChange={e => setEmpresa({ ...empresa, bairro: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
            <input type="text" value={empresa.cidade || ''} onChange={e => setEmpresa({ ...empresa, cidade: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select value={empresa.estado || ''} onChange={e => setEmpresa({ ...empresa, estado: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white">
              <option value="">Selecione</option>
              {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <button type="submit" disabled={isSaving}
          className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar Configurações
        </button>
      </div>
    </form>
  );
}
